import { PaymentUrl } from './urls'
import { NavigateFunction } from 'react-router-dom'
import showResponseError from '../../../utils/showResponseError'
import { t } from 'i18next'
/* eslint-disable camelcase */
import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit'

import { AppHttpConfig as API } from '../../../services/httpConfig'

import toastify from '../../../utils/toastify'
import {
  CreatePaymnetResult,
  IPaymentInitialState,
  TCreatePaymentResult,
  TPaymentList,
  VerifyPaymnetResult,
} from './types'
import { ToastType } from '../../../types/generalType'
import paymentOrder from '../../../utils/paymentOrder'
import queryStringBuilder, { QueryParams } from '../../../utils/queryStringBuilder'
import hasObjectData from '../../../utils/hasObjectData'

const verifyPaymentResult: VerifyPaymnetResult = {
  invoiceId: null,
  cardNumber: '',
  rrn: '',
  refId: '',
  amount: null,
  payGateTranID: null,
  resCode: null,
  resMessage: '',
  payDate: '',
}

const initialState: IPaymentInitialState = {
  loading: false,
  creatPayment: {
    loading: false,
    result: { gatewayUrl: '', refId: '' },
  },
  verifyPayment: {
    loading: false,
    result: verifyPaymentResult,
  },
  payments: {
    loading: false,
    paymentList: { total: 0, data: [] },
    currentPage: 1,
  },
}

// create payment
export const createPayment = createAsyncThunk<
  CreatePaymnetResult,
  { groupId: string; pilgrims: TCreatePaymentResult[] },
  { rejectValue: { errorMessage: string } }
>('payment/createPayment', async (data, thunkAPI) => {
  try {
    const response = await API.post(PaymentUrl.create, data)
      .then((res) => {
        if (res.status === 201) {
          return res.data
        }
        if (res.status === 400) {
          return res.data
        }
      })
      .catch((error) => {
        toastify(ToastType.ERROR, { toastMessage: t('errorInPaymentProccess') })
        return thunkAPI.rejectWithValue(error[0])
      })
    return response
  } catch (error) {
    showResponseError(error)
  }
})
export const getPaymentsByPreReserveId = createAsyncThunk<TPaymentList, QueryParams>(
  'payment/getPaymentsByPreReserveId',
  async (data: QueryParams, thunkAPI) => {
    const query = hasObjectData(data) ? queryStringBuilder(data) : ''
    try {
      const response = await API.get(`${PaymentUrl.GetByPreReserveId}${query}`)
        .then((res) => {
          if (res.status === 200) {
            return res.data
          }
          if (res.status === 400) {
            return res.data
          }
        })
        .catch((error) => {
          toastify(ToastType.ERROR, { toastMessage: t('errorInPaymentProccess') })
          return thunkAPI.rejectWithValue(error[0])
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)
export const verifyPayment = createAsyncThunk<VerifyPaymnetResult, string>(
  'payment/verifyPayment',
  async (invoiceID: string) => {
    try {
      const response = await API.put(PaymentUrl.verify, { invoiceID })
        .then((res) => {
          if (res.status === 201) {
            return res.data
          }
        })
        .catch((error) => {
          showResponseError(error)
        })
      return response
    } catch (error) {
      showResponseError(error)
    }
  },
)

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearVerifyPaymentResult: (state) => {
      state.verifyPayment.result = verifyPaymentResult
    },
    setPaymentListCurrentPage: (state, action: PayloadAction<number>) => {
      state.payments.currentPage = action.payload
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<IPaymentInitialState>) => {
    // create
    builder
      .addCase(createPayment.pending, (state) => {
        state.creatPayment.loading = true
      })
      .addCase(
        createPayment.fulfilled,
        (state, action: PayloadAction<CreatePaymnetResult, any>) => {
          const { gatewayUrl, refId } = action.payload
          state.creatPayment.loading = true
          state.creatPayment.result = action.payload
          paymentOrder(refId, gatewayUrl)
        },
      )
      .addCase(createPayment.rejected, (state) => {
        state.creatPayment.loading = false
        state.creatPayment.result = { gatewayUrl: '', refId: '' }
      })
    // Get Payments By Id
    builder
      .addCase(getPaymentsByPreReserveId.pending, (state) => {
        state.payments.loading = true
      })
      .addCase(
        getPaymentsByPreReserveId.fulfilled,
        (state, action: PayloadAction<TPaymentList, any>) => {
          state.payments.loading = false
          state.payments.paymentList = action.payload
        },
      )
      .addCase(getPaymentsByPreReserveId.rejected, (state) => {
        state.payments.loading = false
        state.payments.paymentList = { total: 0, data: [] }
      })
    // Verify
    builder
      .addCase(verifyPayment.pending, (state) => {
        state.verifyPayment.loading = true
      })
      .addCase(
        verifyPayment.fulfilled,
        (state, action: PayloadAction<VerifyPaymnetResult, any>) => {
          state.verifyPayment.loading = false
          state.verifyPayment.result = action.payload
        },
      )
      .addCase(verifyPayment.rejected, (state) => {
        state.verifyPayment.loading = false
        state.verifyPayment.result = verifyPaymentResult
      })
  },
})

export const { clearVerifyPaymentResult, setPaymentListCurrentPage } = paymentSlice.actions

export default paymentSlice.reducer
